/**
 * Transport abstraction — BG-2 minimum viable surface.
 *
 * Both the Electron app and the future browser SPA call into this
 * interface rather than the Electron-specific `window.crucible.*`
 * global. BG-2 ships:
 *   - this interface (the contract)
 *   - `ElectronCrucibleClient` (wraps the existing `window.crucible`
 *     preload bridge — same shape, same behaviour, behavioural
 *     equivalence preserved)
 *   - the React Context plumbing (`CrucibleClientProvider` +
 *     `useCrucibleClient()`)
 *
 * BG-2 deliberately does NOT migrate the ~50 lifted-renderer call
 * sites that still call `window.crucible.invoke(...)` directly.
 * Those keep working through the Electron preload bridge until BG-3
 * adds the browser-side `BrowserCrucibleClient` + auth flow, at
 * which point per-call-site migrations become meaningful and can
 * land incrementally.
 *
 * Interface shape mirrors `window.crucible`'s `invoke` + `on` so
 * the migration to `useCrucibleClient()` is a mechanical drop-in
 * replacement.
 *
 * Future surface that BG-3 will add to this interface:
 *   - `system<M>(method, params)` — JSON-RPC-only methods that
 *     don't have an IPC counterpart (`session.auth.refresh@1`).
 *     ElectronCrucibleClient throws "unsupported" for these;
 *     BrowserCrucibleClient routes them as plain JSON-RPC calls.
 *   - `connect()` / `close()` lifecycle — Electron is a no-op,
 *     browser actually opens/closes the WSS.
 *   - `state` + `stateChange` AsyncIterable for reconnect UX.
 *   - `InvokeArgs<C>` widens to include `opts?: InvokeOptions` (the
 *     conditional-rest disambiguation per TDD-v2 §2.1 + §2.2). BG-3
 *     also updates `ElectronCrucibleClient.invoke` to detect the
 *     `opts` slot at runtime via `'signal' in rest[0]`, so the
 *     widening is non-breaking for existing callsites (extra
 *     positional is optional).
 *
 * Cross-references:
 *   - TDD-v2 §2.1 — the full surface (BG-3 adds the rest)
 *   - TDD-v2 §2.2 — ElectronCrucibleClient (shipped in this PR)
 *   - TDD-v2 §2.3 — BrowserCrucibleClient (BG-3 deliverable)
 */

import type { InvokeChannelMap, PushChannelMap } from '@volley/crucible-ipc-types';

export type InvokeChannel = keyof InvokeChannelMap;
export type PushChannel = keyof PushChannelMap;

/**
 * Unsubscribes a push-channel subscription. Calling it idempotently
 * is allowed — second + later calls are no-ops.
 */
export interface Unsubscribe {
  (): void;
}

/**
 * Per-call options threaded through `invoke()`. Currently:
 *
 *   - `signal` — `AbortSignal` for cancellation. Wired to the
 *     JSON-RPC `request.cancel@1` notification by
 *     `BrowserCrucibleClient`; ignored by `ElectronCrucibleClient`
 *     today (Electron-side cancellation lands as a BG-5+ follow-up
 *     because the existing IPC dispatcher doesn't thread it).
 */
export interface InvokeOptions {
  signal?: AbortSignal;
}

/**
 * Conditional rest-args type so the call shape matches what
 * `window.crucible.invoke` accepts AND lets BG-3+ thread per-call
 * options through (`signal`, future telemetry slots):
 *
 *   client.invoke('game:list')                              // [void, _] — no args
 *   client.invoke('game:list', { signal })                  // void-arg + opts
 *   client.invoke('agent:send', { messages })               // [Args, _] — args required
 *   client.invoke('agent:send', { messages }, { signal })   // args + opts
 *
 * Channels typed as `[void, T]` in the ChannelMap take an optional
 * `InvokeOptions` slot; channels typed as `[Args, T]` take exactly
 * one args parameter then an optional `InvokeOptions`. The widening
 * is non-breaking — every pre-BG-3 call site continues to typecheck
 * because the `opts` slot is optional.
 */
export type InvokeArgs<C extends InvokeChannel> = InvokeChannelMap[C][0] extends void
  ? [opts?: InvokeOptions]
  : [args: InvokeChannelMap[C][0], opts?: InvokeOptions];

/**
 * JSON-RPC-only methods that don't have an IPC counterpart. Used by
 * the auth module for in-session token refresh + by future
 * observability surfaces. `ElectronCrucibleClient` rejects calls to
 * `system()` with an "unsupported in Electron transport" error
 * because IPC doesn't speak these methods; `BrowserCrucibleClient`
 * routes them as plain JSON-RPC requests against the cap host.
 *
 * The map mirrors `InvokeChannelMap`'s tuple shape: `[Args, Result]`.
 */
export interface SystemMethodMap {
  /**
   * Mid-session bearer-token refresh — see `docs/cloud-vm/design/jsonrpc-channel.md`.
   * The auth worker calls this when its short-lived JWT is rotating;
   * the server validates the refresh token + emits a fresh access
   * token without tearing down the WebSocket.
   */
  'session.auth.refresh@1': [{ refreshToken: string }, { accessToken: string; expiresIn: number }];
  /** Round-trip smoke probe — returns the request payload unchanged. */
  'system.echo@1': [{ payload: unknown }, { payload: unknown }];
  /** Runtime version + supported method versions. */
  'system.info@1': [
    void,
    {
      runtimeVersion: string;
      supportedMethodVersions: Record<string, number[]>;
      guiCapabilities?: RuntimeGuiCapabilities;
    },
  ];
}

export type SystemMethod = keyof SystemMethodMap;

export interface RuntimeGuiCapabilities {
  hasRuntimePreview?: boolean;
  canPublishRun?: boolean;
  ingotScaffolderAvailable?: boolean;
}

/**
 * Connection state surfaced to callers via `client.state`. Reflects
 * the underlying transport's lifecycle, not auth state. The browser
 * SPA uses this to render reconnection banners.
 */
export type ClientState = 'closed' | 'connecting' | 'open' | 'reconnecting';

/**
 * Streaming invocation handle — returned by `invokeStreaming(...)`.
 * Pairs the final-response promise (`result`) with an
 * `AsyncIterable<Chunk>` of mid-flight chunks (e.g.
 * `agent.send.chunk@1` notifications carrying `params.requestId`).
 *
 * Iteration ends when the server emits the final response envelope
 * for the request. Callers can consume `chunks` and `result` in
 * either order — the iterable produces chunks as they arrive, and
 * `await result` resolves with the final response shape.
 *
 * `cancel()` aborts the in-flight call (emits `request.cancel@1`)
 * AND closes the chunk iterable so awaiting consumers see a clean
 * loop termination + the `result` promise rejects with a typed
 * `CrucibleError` (`code='CRUCIBLE-499'`).
 *
 * Type parameter `ChunkT` is opaque at the interface level (the
 * `InvokeChannelMap` only types the final-response payload). Per-
 * channel chunk types are documented in JSDoc on the calling
 * surface (e.g. agent:send → `AgentStreamChunk`).
 */
export interface StreamingInvocation<Result, ChunkT = unknown> {
  /** Iterable of mid-flight chunks. Terminates when result resolves. */
  chunks: AsyncIterable<ChunkT>;
  /** Promise resolving the final response envelope. */
  result: Promise<Result>;
  /** Abort the in-flight call + close the chunk iterable. */
  cancel(): void;
}

/**
 * The transport contract. The first two methods (`invoke` +
 * `subscribe`) cover the existing `window.crucible` surface 1:1 so
 * the renderer's lifted call sites continue to work unchanged
 * through both transports.
 *
 * BG-5 adds the JSON-RPC-only surface (`system()` +
 * `connect()` / `close()` lifecycle + `state` getter +
 * `drainForBfcache()`) needed by the browser SPA. Electron's
 * `IPC`-bridge transport implements these as no-ops / unsupported.
 */
export interface CrucibleClient {
  /**
   * The consumer environment's capability matrix. Co-located with
   * the client (instance property) so the bootstrap can't accidentally
   * pair `new BrowserCrucibleClient()` with `ElectronCrucibleClient`'s
   * capabilities and vice-versa (codex round-1 L1/M2). Each
   * implementation forwards from a static class member for now;
   * BG-3+ may switch to a per-runtime probe.
   */
  readonly capabilities: ClientCapabilities;

  /**
   * Subscribe to capability snapshot changes. Browser cloud mode updates this
   * matrix after runtime handshakes; React providers use this hook to re-render
   * gate consumers without swapping the whole client instance.
   */
  subscribeCapabilities?(handler: () => void): Unsubscribe;

  /**
   * Send a request to the main process / cloud-VM runtime and await
   * the response. Channel + args + return type are pulled from
   * `@volley/crucible-ipc-types`'s `InvokeChannelMap`.
   *
   * Errors are surfaced as rejected promises. BG-3 will introduce a
   * `transport/error-mapping` module that wraps the underlying
   * error envelope into a typed `CrucibleError`; BG-2 returns the
   * raw rejection from the underlying transport (window.crucible's
   * preload bridge already produces a structured envelope, so
   * Electron-side consumers can read `.code` + `.message` directly).
   */
  invoke<C extends InvokeChannel>(
    channel: C,
    ...rest: InvokeArgs<C>
  ): Promise<InvokeChannelMap[C][1]>;

  /**
   * Subscribe to a push channel. The returned `Unsubscribe` removes
   * the listener. Multiple subscriptions to the same channel are
   * supported — each handler receives every payload.
   *
   * Naming note: this method is called `subscribe` (not `on`) so the
   * interface reads as a transport abstraction rather than a thin
   * pass-through of `window.crucible.on`. The ElectronCrucibleClient
   * adapter forwards to `window.crucible.on` under the hood.
   */
  subscribe<E extends PushChannel>(
    event: E,
    handler: (payload: PushChannelMap[E]) => void,
  ): Unsubscribe;

  /**
   * Invoke a JSON-RPC-only system method (no IPC counterpart).
   * `ElectronCrucibleClient` rejects every call with an
   * "unsupported" `CrucibleError`; `BrowserCrucibleClient` routes
   * the call as a plain JSON-RPC request. Auth modules call into
   * this for mid-session token refresh.
   */
  system<M extends SystemMethod>(
    method: M,
    params: SystemMethodMap[M][0],
  ): Promise<SystemMethodMap[M][1]>;

  /**
   * Open the underlying transport. `ElectronCrucibleClient` is a
   * no-op (IPC is always available); `BrowserCrucibleClient` opens
   * the WSS connection + awaits `session.welcome@1` or
   * `session.resumed@1` before resolving.
   */
  connect(): Promise<void>;

  /**
   * Close the underlying transport. `ElectronCrucibleClient` is a
   * no-op; `BrowserCrucibleClient` closes the WebSocket with code
   * 1000 and clears its in-flight pending map.
   */
  close(): Promise<void>;

  /**
   * Current transport state. Electron is always `'open'`; the
   * browser surface transitions through `'closed' → 'connecting' →
   * 'open' → 'reconnecting' → ...`. UI uses this to render
   * reconnect banners.
   */
  readonly state: ClientState;

  /**
   * Drain in-flight requests + subscriber state in preparation for
   * a bfcache eviction (`pagehide` with `event.persisted === true`).
   * Each pending request rejects with a retryable
   * `bfcache-restore` `CrucibleError`; the call site's caller is
   * expected to retry on `pageshow`. `ElectronCrucibleClient` is a
   * no-op (no bfcache in Electron).
   */
  drainForBfcache(): void;

  /**
   * Invoke a streaming channel — returns a `StreamingInvocation`
   * pairing the final-response promise with an `AsyncIterable` of
   * mid-flight chunks. Per TDD-v2 §2.3 + plan-v2 gate 9.
   *
   * Today the only streaming channel is `agent:send`; the chunks
   * are `agent.send.chunk@1` notifications carrying
   * `params.requestId` matching the invocation's request id.
   *
   * `ElectronCrucibleClient` throws "unsupported" because Electron
   * IPC doesn't carry the request-id correlation needed for chunk
   * routing; renderers consuming agent chunks under Electron
   * subscribe to `'agent:stream'` instead. `BrowserCrucibleClient`
   * routes chunks via the JSON-RPC notification path.
   */
  invokeStreaming<C extends InvokeChannel, ChunkT = unknown>(
    channel: C,
    args: InvokeChannelMap[C][0],
    opts?: InvokeOptions,
  ): StreamingInvocation<InvokeChannelMap[C][1], ChunkT>;
}

/**
 * What the consumer's environment supports. Used by the renderer to
 * gate Electron-only affordances (auto-updater pane, VWR device
 * pairing, local-filesystem game discovery, etc.) in the browser SPA.
 *
 * BG-2 ships a static `capabilities` object on each client class;
 * BG-3 + later phases may switch to a per-runtime probe if a
 * per-tenant capability gate becomes useful.
 */
export interface ClientCapabilities {
  /**
   * True for Electron; false for browser. UI uses this to gate
   * local-filesystem operations (file picker, real-FS game tree).
   */
  hasLocalFilesystem: boolean;
  /**
   * True for Electron; false for browser. UI uses this to gate the
   * auto-updater pane in Settings — the browser SPA's "update
   * available" surface is the version-poll banner (BG-4).
   */
  hasAutoUpdater: boolean;
  /**
   * True for Electron; false for browser. UI uses this to gate VWR
   * (Volley Wireless Remote) device pairing flows that only work
   * over local network.
   */
  hasLocalNetwork: boolean;
  /**
   * True when the current transport can service the invoke channel with a real
   * local/runtime implementation. Browser cloud mode populates this from the
   * runtime's advertised method set; Electron treats local IPC as available.
   */
  canInvoke?: (channel: InvokeChannel) => boolean;
  /** True when the runtime can start/restart cloud preview sessions. */
  hasRuntimePreview?: boolean;
  /** True when deploy-to-TV is backed by a real runtime publish dispatcher. */
  canPublishRun?: boolean;
  /** True when create-time Ingot/component-template scaffolding is backed. */
  ingotScaffolderAvailable?: boolean;
  /**
   * True when this renderer can open the NATIVE ProtoHub simulator
   * (the Electron `BrowserView` opened by the `protohub:*` ipcMain
   * handlers) — i.e. the renderer is backed by a preload bridge.
   *
   * Electron renderers: true. The cookie-mode cloud SPA and pure-web
   * SPA run preload-less (`BrowserCrucibleClient`), so `protohub:*`
   * has no transport to Electron main — it would otherwise fall
   * through to the pod and return `Method not found: protohub.open@1`
   * (crucible#2325). When false, the Workspace must NOT auto-open the
   * in-SPA ProtoHub surface. Defaults to {@link hasLocalFilesystem}
   * when unset (the Electron-vs-browser discriminator).
   */
  hasNativeSimulator?: boolean;
}

export function canInvokeChannel(
  capabilities: ClientCapabilities,
  channel: InvokeChannel,
): boolean {
  return capabilities.canInvoke?.(channel) ?? true;
}

export function hasRuntimePreviewCapability(capabilities: ClientCapabilities): boolean {
  return capabilities.hasRuntimePreview ?? capabilities.hasLocalFilesystem;
}

export function canPublishRun(capabilities: ClientCapabilities): boolean {
  return capabilities.canPublishRun ?? true;
}

export function canBuildWithIngots(capabilities: ClientCapabilities): boolean {
  return capabilities.ingotScaffolderAvailable ?? true;
}

/**
 * True when this renderer can open the native ProtoHub simulator
 * ("Display on Hub" / post-deploy preview). Falls back to
 * {@link ClientCapabilities.hasLocalFilesystem} (the
 * Electron-vs-browser discriminator) when `hasNativeSimulator` is
 * unset. See crucible#2325.
 */
export function canDisplayOnHub(capabilities: ClientCapabilities): boolean {
  return capabilities.hasNativeSimulator ?? capabilities.hasLocalFilesystem;
}
